import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { SlidersHorizontal, Loader2, Save, RefreshCw, Send, Activity } from "lucide-react";
import { TelegramCrossAlerts } from "@/components/TelegramCrossAlerts";

export default function Admin() {
  const [tab, setTab] = useState("users");
  const [users, setUsers] = useState([]);
  const [settings, setSettings] = useState(null);
  const [monitor, setMonitor] = useState(null);
  const [dhan, setDhan] = useState(null);
  const [dhanTok, setDhanTok] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [u, s, m, d] = await Promise.all([
      api.get("/admin/users"), api.get("/admin/settings"),
      api.get("/admin/monitor"), api.get("/admin/dhan/status"),
    ]);
    setUsers(u.data.users); setSettings(s.data); setMonitor(m.data); setDhan(d.data); setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const dhanAction = async (fn, msg) => {
    try { const r = await fn(); toast.success(msg); if (msg === "Token saved") setDhanTok(""); await load(); return r; }
    catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
  };

  const dhanConnect = async () => {
    try {
      const { data } = await api.post("/admin/dhan/connect");
      window.open(data.login_url, "_blank");
      toast.success("Opening Dhan login — authorize to connect");
    } catch (e) { toast.error(e.response?.data?.detail || "Connect failed"); }
  };

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get("dhan") === "connected") { toast.success("Dhan connected via OAuth"); window.history.replaceState({}, "", "/app/admin"); }
    else if (p.get("dhan") === "error") { toast.error(`Dhan connect failed: ${p.get("msg") || ""}`); window.history.replaceState({}, "", "/app/admin"); }
  }, []);

  const updateUser = async (id, patch) => {
    await api.patch(`/admin/users/${id}`, patch);
    toast.success("User updated");
    load();
  };
  const saveSettings = async () => {
    setSaving(true);
    try { await api.patch("/admin/settings", settings); toast.success("Scanner settings saved"); }
    finally { setSaving(false); }
  };
  const scanNow = async () => { const { data } = await api.post("/scanner/scan-now"); toast.success(`${data.created} signals created`); load(); };

  if (loading) return <div className="p-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-blue-600" /></div>;

  const setS = (k, v) => setSettings((s) => ({ ...s, [k]: v }));
  const st = monitor.stats;

  return (
    <div>
      <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2 mb-5">
        <SlidersHorizontal className="h-6 w-6 text-blue-600" /> Admin Portal
      </h1>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-white border border-slate-200 max-w-full overflow-x-auto justify-start">
          <TabsTrigger value="users" data-testid="admin-tab-users">Users</TabsTrigger>
          <TabsTrigger value="tuning" data-testid="admin-tab-tuning">Scanner Tuning</TabsTrigger>
          <TabsTrigger value="monitor" data-testid="admin-tab-monitor">Alert Monitor</TabsTrigger>
          <TabsTrigger value="feed" data-testid="admin-tab-feed">Data Feed</TabsTrigger>
        </TabsList>

        {/* USERS */}
        <TabsContent value="users" className="mt-4">
          <Card className="bg-white border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader><TableRow className="border-slate-200 hover:bg-transparent">
                  {["User", "Role", "Plan", "Status", "Actions"].map((h) => <TableHead key={h} className="text-[10px] tracking-[0.15em] text-slate-500 uppercase">{h}</TableHead>)}
                </TableRow></TableHeader>
                <TableBody>
                  {users.map((u) => (
                    <TableRow key={u.id} className="border-slate-200" data-testid={`admin-user-${u.email}`}>
                      <TableCell><div className="font-medium text-slate-900">{u.name}</div><div className="text-[11px] text-slate-500 font-mono">{u.email}</div></TableCell>
                      <TableCell><Badge variant="secondary" className={u.role === "admin" ? "bg-blue-100/80 text-blue-700" : "bg-slate-100 text-slate-500"}>{u.role}</Badge></TableCell>
                      <TableCell className="font-mono text-sm text-slate-700 capitalize">{u.plan}</TableCell>
                      <TableCell><Badge className={u.subscription?.active ? "bg-emerald-100/80 text-emerald-700" : "bg-rose-100/80 text-rose-700"}>{u.subscription?.status}</Badge></TableCell>
                      <TableCell>
                        <div className="flex gap-1.5">
                          <Button size="sm" variant="outline" className="border-slate-300 h-7 text-xs" onClick={() => updateUser(u.id, { extend_days: 30 })} data-testid={`admin-extend-${u.email}`}>+30d Pro</Button>
                          <Button size="sm" variant="outline" className="border-slate-300 h-7 text-xs" onClick={() => updateUser(u.id, { subscription_status: "expired" })}>Expire</Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>
        </TabsContent>

        {/* TUNING */}
        <TabsContent value="tuning" className="mt-4">
          <Card className="bg-white border-slate-200 p-6 max-w-2xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-bold text-slate-900">Range Matrix Breakout Parameters</h3>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500">Engine</span>
                <Switch checked={settings.engine_enabled} onCheckedChange={(v) => setS("engine_enabled", v)} data-testid="engine-toggle" />
              </div>
            </div>
            <div className="flex items-center justify-between rounded-md bg-white/75 border border-slate-200 px-4 py-3 mb-4">
              <div>
                <div className="text-sm font-medium text-slate-700">EMA Stack Cross Alerts</div>
                <div className="text-xs text-slate-500">Alert when price reclaims / loses the 8/13/21 EMA stack on the 5-min chart</div>
              </div>
              <Switch checked={!!settings.ema_alerts_enabled} onCheckedChange={(v) => setS("ema_alerts_enabled", v)} data-testid="ema-alerts-toggle" />
            </div>
            <div className="flex items-center justify-between rounded-md bg-white/75 border border-slate-200 px-4 py-3 mb-4">
              <div>
                <div className="text-sm font-medium text-slate-700">Range Matrix Breakout EMA Confirmation</div>
                <div className="text-xs text-slate-500">Require the breakout to be on the right side of an EMA to cut false signals</div>
              </div>
              <Select value={settings.ema_confirm || "stack"} onValueChange={(v) => setS("ema_confirm", v)}>
                <SelectTrigger className="w-44 bg-white border-slate-300 font-mono" data-testid="ema-confirm-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-300">
                  <SelectItem value="stack">8/13/21 stack (strict)</SelectItem>
                  <SelectItem value="ema21">EMA21 only</SelectItem>
                  <SelectItem value="off">Off (pure Range Matrix)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                ["adr_window", "Range Matrix Window (days)", 1],
                ["breakout_lookback", "Breakout Lookback (days)", 1],
                ["range_multiplier", "Range Expansion Multiplier", 0.1],
                ["volume_multiplier", "Volume Multiplier", 0.1],
                ["cooldown_minutes", "Cooldown (minutes)", 1],
                ["breakout_buffer_pct", "Breakout Buffer (%)", 0.05],
                ["scan_interval", "Scan Interval (sec)", 1],
              ].map(([k, label, step]) => (
                <div key={k}>
                  <Label className="text-xs text-slate-500">{label}</Label>
                  <Input type="number" step={step} value={settings[k]} onChange={(e) => setS(k, Number(e.target.value))} className="bg-white border-slate-300 font-mono mt-1.5" data-testid={`setting-${k}`} />
                </div>
              ))}
            </div>
            <Button onClick={saveSettings} disabled={saving} className="mt-5 bg-blue-600 hover:bg-blue-700" data-testid="save-settings-button">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />} Save parameters
            </Button>
          </Card>
        </TabsContent>

        {/* MONITOR */}
        <TabsContent value="monitor" className="mt-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            <Mini label="Total Signals" value={st.total_signals} />
            <Mini label="Active" value={st.active_signals} cls="text-emerald-600" />
            <Mini label="Users" value={st.users} />
            <Mini label="Provider" value={st.provider} cls="text-cyan-600" mono />
          </div>
          <Card className="bg-white border-slate-200 p-4 mb-4 flex items-center justify-between flex-wrap gap-3" data-testid="scanner-guide-card">
            <div>
              <div className="font-display font-bold text-slate-900">Scanner Guide (private)</div>
              <div className="text-xs text-slate-500">Every TFR scanner explained in plain words — for you only, not shown to members or the public.</div>
            </div>
            <button onClick={async () => {
              const r = await api.get("/admin/scanner-guide", { responseType: "blob" });
              const url = URL.createObjectURL(r.data);
              Object.assign(document.createElement("a"), { href: url, download: "TFR-Scanner-Guide-PRIVATE.pdf" }).click();
              URL.revokeObjectURL(url);
            }} className="px-4 py-2 text-sm font-semibold rounded-md bg-slate-900 text-white hover:bg-slate-700 transition-colors" data-testid="scanner-guide-download">Download guide PDF</button>
          </Card>
          <TelegramCrossAlerts />
          <Card className="bg-white border-slate-200 p-4 mb-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-2"><Send className="h-4 w-4 text-cyan-600" />
                <span className="text-sm text-slate-700">Telegram: <span className="font-mono">{st.telegram_configured ? "LIVE" : "MOCKED"}</span> · sent {st.telegram_sent} · mocked {st.telegram_mocked} · failed {st.telegram_failed}</span>
              </div>
              <Button size="sm" variant="outline" className="border-slate-300" onClick={scanNow} data-testid="admin-scan-now"><RefreshCw className="h-4 w-4 mr-1" /> Trigger scan</Button>
            </div>
          </Card>
          <Card className="bg-white border-slate-200 overflow-hidden">
            <div className="px-4 py-2.5 border-b border-slate-200 text-[10px] tracking-[0.15em] text-slate-500 flex items-center gap-2"><Activity className="h-3.5 w-3.5" /> ALERT AUDIT LOG</div>
            <div className="overflow-x-auto max-h-96 overflow-y-auto">
              <Table>
                <TableBody>
                  {monitor.audit.map((a) => (
                    <TableRow key={a.id} className="border-slate-200">
                      <TableCell className="font-mono text-xs text-slate-500">{new Date(a.created_at).toLocaleTimeString()}</TableCell>
                      <TableCell className="font-display font-semibold text-slate-700">{a.symbol}</TableCell>
                      <TableCell><Badge className={a.direction === "BULLISH" ? "bg-emerald-100/80 text-emerald-700" : "bg-rose-100/80 text-rose-700"}>{a.direction}</Badge></TableCell>
                      <TableCell className="font-mono text-xs text-slate-500">{a.timeframe}</TableCell>
                      <TableCell className="font-mono text-xs text-slate-500">{a.event}</TableCell>
                      <TableCell><Badge variant="secondary" className="bg-slate-100 text-slate-500 text-[10px]">{a.telegram_status}</Badge></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>
        </TabsContent>

        {/* DATA FEED (Dhan) */}
        <TabsContent value="feed" className="mt-4">
          {dhan && (
            <div className="space-y-4 max-w-3xl">
              <Card className="bg-white border-slate-200 p-5">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div className="flex items-center gap-2">
                    <Activity className="h-5 w-5 text-emerald-600" />
                    <h3 className="font-display font-bold text-slate-900">Dhan Data Feed</h3>
                    <Badge className={dhan.connected ? "bg-emerald-100/80 text-emerald-700" : "bg-rose-100/80 text-rose-700"}>
                      {dhan.connected ? "Connected" : "Not connected"}
                    </Badge>
                    <Badge variant="secondary" className={dhan.feed_running ? "bg-cyan-100/80 text-cyan-700" : "bg-slate-100 text-slate-500"}>
                      Feed {dhan.feed_running ? "running" : "stopped"}
                    </Badge>
                  </div>
                  <span className="font-mono text-xs text-slate-500">client {dhan.client_id}</span>
                </div>
                {dhan.live_enabled === false && (
                  <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600" data-testid="dhan-preview-notice">
                    Preview environment — Dhan live connection is disabled here so it never cancels the live app's token. Connect Dhan from the live site.
                  </div>
                )}
                {dhan.reauth_required && (
                  <div className="mt-3 rounded-lg border border-rose-300 bg-rose-50 px-3 py-2 text-xs text-rose-700 font-semibold" data-testid="dhan-reauth-alert">
                    Dhan session was cancelled or expired. Click "Connect Dhan" below to reconnect. ({dhan.reauth_reason})
                  </div>
                )}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
                  <Mini label="Resolved Underlyings" value={dhan.resolved_underlyings} />
                  <Mini label="Live Quotes" value={dhan.live_quotes} cls="text-cyan-600" />
                  <Mini label="Token Source" value={dhan.token_source || "—"} mono cls="text-slate-700" />
                  <Mini label="Token Expiry" value={dhan.token_expiry ? new Date(dhan.token_expiry).toLocaleString() : "—"} mono cls="text-slate-700" />
                </div>
                <div className="flex flex-wrap items-center justify-between gap-2 text-xs font-mono text-slate-500 mt-3">
                  <span>Auto-renew: <span className={dhan.auto_renew_running ? "text-emerald-600" : "text-slate-500"}>{dhan.auto_renew_running ? "on · renews <3h before expiry" : "off"}</span></span>
                  <span>Renewals: {dhan.renew_count}{dhan.last_renew_at ? ` · last ${new Date(dhan.last_renew_at).toLocaleString()}` : ""}</span>
                </div>
                <p className="text-[11px] text-amber-700 font-mono mt-3 bg-amber-100/70 border border-amber-300/60 rounded-md px-3 py-2">
                  {dhan.historical_note}. Live WebSocket feed streams real LTP during market hours (09:15–15:30 IST); off-hours the connection idles.
                </p>
              </Card>

              <Card className="bg-white border-slate-200 p-5 space-y-4">
                <div>
                  <h4 className="font-display font-semibold text-slate-900 mb-1">Update access token (24h)</h4>
                  <p className="text-xs text-slate-500 mb-2">Paste a fresh token from Dhan Web → Access DhanHQ APIs. Stored server-side only.</p>
                  <div className="flex gap-2">
                    <Input type="password" value={dhanTok} onChange={(e) => setDhanTok(e.target.value)} placeholder="Paste Dhan access token" className="bg-white border-slate-300 font-mono" data-testid="dhan-token-input" />
                    <Button className="bg-blue-600 hover:bg-blue-700" disabled={!dhanTok.trim()} onClick={() => dhanAction(() => api.post("/admin/dhan/use-token", { access_token: dhanTok.trim() }), "Token saved")} data-testid="dhan-save-token">Save</Button>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 pt-1">
                  <Button size="sm" className="bg-cyan-600 hover:bg-cyan-500" onClick={dhanConnect} data-testid="dhan-oauth-connect">Connect with Dhan (OAuth)</Button>
                  <Button size="sm" variant="outline" className="border-slate-300" onClick={() => dhanAction(() => api.post("/admin/dhan/renew"), "Token renewed")} data-testid="dhan-renew">Renew now</Button>
                  <Button size="sm" variant="outline" className="border-slate-300" onClick={() => dhanAction(() => api.post("/admin/dhan/sync"), "Instruments synced")} data-testid="dhan-sync">Sync instruments</Button>
                  <Button size="sm" variant="outline" className="border-slate-300" onClick={() => dhanAction(() => api.post("/admin/dhan/backfill"), "Backfill complete")} data-testid="dhan-backfill">Backfill candles (PLUS)</Button>
                  {dhan.feed_running ? (
                    <Button size="sm" variant="outline" className="border-rose-400/60 text-rose-700" onClick={() => dhanAction(() => api.post("/admin/dhan/stop-feed"), "Feed stopped")}>Stop feed</Button>
                  ) : (
                    <Button size="sm" variant="outline" className="border-emerald-400/60 text-emerald-700" onClick={() => dhanAction(() => api.post("/admin/dhan/start-feed"), "Feed started")} data-testid="dhan-start-feed">Start feed</Button>
                  )}
                  <Button size="sm" variant="ghost" className="text-slate-500" onClick={load}><RefreshCw className="h-4 w-4 mr-1" /> Refresh</Button>
                </div>
              </Card>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Mini({ label, value, cls = "text-slate-900", mono }) {
  return (
    <Card className="bg-white border-slate-200 p-4">
      <div className="text-[10px] tracking-[0.15em] text-slate-500">{label}</div>
      <div className={`text-xl mt-1.5 ${cls} ${mono ? "font-mono" : "font-display font-bold"}`}>{value}</div>
    </Card>
  );
}
